import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ExternalLink, Link2, MapPin, Monitor, Plus, Users } from 'lucide-react';
import dayjs from 'dayjs';

import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { StaffConversationDetail, SupportTask } from '../../../common/models/support.model';
import { CONVERSATION_STATUS_META, TASK_TYPE_META } from '../support-meta';
import { TaskBadge } from '../components/TaskBadge';

const Section = ({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) => (
  <section className="flex flex-col gap-2 border-b px-4 py-4 last:border-b-0">
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-xs font-medium text-muted-foreground">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

const Row = ({ icon: Icon, children }: { icon: typeof MapPin; children: React.ReactNode }) => (
  <p className="flex items-start gap-2 text-xs">
    <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
    <span className="min-w-0 break-words">{children}</span>
  </p>
);

function shortAgent(ua?: string) {
  if (!ua) return undefined;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'macOS' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Linux/.test(ua) ? 'Linux' : '';
  return [browser, os].filter(Boolean).join(' · ');
}

/** Right pane: the vendor, where they were, the tasks this raised, and their other conversations. */
export function ConversationDetails({
  detail,
  allTasks,
  onRaiseTask,
  onLinkTask,
  onOpenConversation,
}: {
  detail: StaffConversationDetail;
  allTasks: SupportTask[];
  onRaiseTask: () => void;
  onLinkTask: (taskId: string) => void;
  onOpenConversation: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { vendor, conversation, tasks, history } = detail;
  const ctx = conversation.context;
  const [linking, setLinking] = useState(false);
  const linkable = useMemo(() => allTasks.filter((task) => !tasks.some((x) => x._id === task._id)), [allTasks, tasks]);

  return (
    <div className="flex min-h-0 flex-col overflow-y-auto" data-testid="conversation-details">
      {vendor && (
        <Section title={t('SUPPORT.STAFF.VENDOR')}>
          <div className="flex items-center gap-3">
            <span className="flex size-9 items-center justify-center rounded-xl bg-muted text-sm font-medium uppercase">{vendor.name?.charAt(0)}</span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{vendor.name}</p>
              <p className="truncate text-xs text-muted-foreground">{vendor.email || vendor.alias}</p>
            </div>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg border px-2.5 py-2">
              <p className="text-muted-foreground">{t('SUPPORT.STAFF.PLAN')}</p>
              <p className="truncate font-medium">{vendor.planName || '—'}</p>
            </div>
            <div className="rounded-lg border px-2.5 py-2">
              <p className="flex items-center gap-1 text-muted-foreground"><Users className="size-3" />{t('SUPPORT.STAFF.SEATS')}</p>
              <p className="font-medium tabular-nums">
                {vendor.seats.inUse ?? '—'} / {vendor.seats.limit < 0 ? '∞' : vendor.seats.limit}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to={`/vendors/${vendor._id}/edit`}>{t('SUPPORT.STAFF.OPEN_VENDOR')}</Link>
            </Button>
            {vendor.storeUrl && (
              <Button asChild variant="ghost" size="sm">
                <a href={vendor.storeUrl} target="_blank" rel="noreferrer"><ExternalLink data-icon="inline-start" />{t('SUPPORT.STAFF.STORE')}</a>
              </Button>
            )}
          </div>
        </Section>
      )}

      {ctx && (ctx.page || ctx.url || ctx.userAgent) && (
        <Section title={t('SUPPORT.STAFF.CONTEXT')}>
          {(ctx.page || ctx.url) && (
            <Row icon={MapPin}>
              {ctx.url ? <a href={ctx.url} target="_blank" rel="noreferrer" className="underline-offset-2 hover:underline">{ctx.page || ctx.url}</a> : ctx.page}
            </Row>
          )}
          {ctx.userAgent && (
            <Row icon={Monitor}>
              {[shortAgent(ctx.userAgent), ctx.viewport, ctx.locale].filter(Boolean).join(' · ')}
            </Row>
          )}
          <p className="text-[11px] text-muted-foreground">
            {t('SUPPORT.STAFF.OPENED', { when: dayjs(conversation.createdAt).format('D MMM YYYY, HH:mm') })}
          </p>
        </Section>
      )}

      <Section
        title={t('SUPPORT.STAFF.TASKS')}
        action={
          <div className="flex gap-1">
            <Popover open={linking} onOpenChange={setLinking}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon-sm" aria-label={t('SUPPORT.STAFF.LINK_TASK')} title={t('SUPPORT.STAFF.LINK_TASK')}>
                  <Link2 />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <Command>
                  <CommandInput placeholder={t('SUPPORT.STAFF.FIND_TASK')} />
                  <CommandList>
                    <CommandEmpty>{t('SUPPORT.STAFF.NO_TASK')}</CommandEmpty>
                    {linkable.map((task) => {
                      const Icon = TASK_TYPE_META[task.type].icon;
                      return (
                        <CommandItem
                          key={task._id}
                          value={`#${task.number} ${task.title} ${task.vendorName || ''}`}
                          onSelect={() => {
                            setLinking(false);
                            onLinkTask(task._id);
                          }}
                        >
                          <Icon className={cn('size-3.5', TASK_TYPE_META[task.type].tone)} />
                          <span className="text-muted-foreground tabular-nums">#{task.number}</span>
                          <span className="truncate">{task.title}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <Button variant="ghost" size="icon-sm" onClick={onRaiseTask} aria-label={t('SUPPORT.STAFF.RAISE_TASK')} title={t('SUPPORT.STAFF.RAISE_TASK')}>
              <Plus />
            </Button>
          </div>
        }
      >
        {tasks.length ? (
          <div className="flex flex-col gap-1.5">
            {tasks.map((task) => (
              <Link key={task._id} to={`/support/board?task=${task._id}`} className="min-w-0">
                <TaskBadge task={task} className="w-full hover:bg-muted/60" />
              </Link>
            ))}
          </div>
        ) : (
          <Button variant="outline" size="sm" onClick={onRaiseTask} className="self-start">
            <Plus data-icon="inline-start" />
            {t('SUPPORT.STAFF.RAISE_TASK')}
          </Button>
        )}
      </Section>

      {history.length > 0 && (
        <Section title={t('SUPPORT.STAFF.HISTORY')}>
          {history.map((h) => (
            <button key={h._id} type="button" onClick={() => onOpenConversation(h._id)} className="flex items-center gap-2 rounded-lg px-1 py-1 text-start text-xs hover:bg-muted/60">
              <span className={cn('size-1.5 shrink-0 rounded-full', CONVERSATION_STATUS_META[h.status].dot)} />
              <span className="min-w-0 flex-1 truncate">{h.subject}</span>
              <span className="shrink-0 text-muted-foreground">{dayjs(h.lastMessageAt).format('D MMM')}</span>
            </button>
          ))}
        </Section>
      )}
    </div>
  );
}
